import {
  useDeleteLegalClientFirstHead,
  useLegalClientFirstHeadArchive,
  useLegalClientFirstHeadDearchive,
} from 'api/requests';
import {
  LegalClientEntity,
  LegalClientLegalHeadEntity,
  PermissionType,
} from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useDialog } from 'libs/ui/Dialog/useDialog';

export const useLegalClientLegalHeadFirstHeadActions = (
  legalClient: LegalClientEntity | null,
  legalClientLegalHead: LegalClientLegalHeadEntity | null,
  refetchLegalClientLegalHead: () => void
) => {
  const { t } = useTranslation();

  const notify = useNotify();

  const { checkPermissions } = usePermissions();

  const { deleteLegalClientFirstHead, error: deleteFirstHeadError } =
    useDeleteLegalClientFirstHead();

  const { open: openAddFirstHeadDialog, ...dialogAddFirstHeadDialogProps } =
    useDialog();

  const { archive: archiveFirstHead, error: archiveFirstHeadError } =
    useLegalClientFirstHeadArchive();
  const { dearchive: dearchiveFirstHead, error: dearchiveFirstHeadError } =
    useLegalClientFirstHeadDearchive();

  // Delete first head
  const onDeleteFirstHead = checkPermissions([
    PermissionType.LegalClientHead_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await deleteLegalClientFirstHead({
            id,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          notify.info(
            t('client.legal.legalClientFirstHead.successDeleteMessage')
          );
          refetchLegalClientLegalHead();
        }
      }
    : undefined;

  // Archive first head
  const onArchiveFirstHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await archiveFirstHead(id, {
            reason,
            versionNumber: legalClientLegalHead?.versionNumber!,
          })
        ) {
          await refetchLegalClientLegalHead();
          notify.info(t('common.archivedMessage'));
        }
      }
    : undefined;

  // Dearchive first head
  const onDearchiveFirstHead = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        if (
          await dearchiveFirstHead(id, {
            reason,
            versionNumber: legalClientLegalHead?.versionNumber!,
          })
        ) {
          await refetchLegalClientLegalHead();
          notify.info(t('common.dearchivedMessage'));
        }
      }
    : undefined;

  // Open add first head dialog
  const onClickAddFirstHead = checkPermissions([
    PermissionType.LegalClientChildEntity_CreateOne,
  ])
    ? openAddFirstHeadDialog
    : undefined;

  useServerErrorNotify(
    deleteFirstHeadError ?? archiveFirstHeadError ?? dearchiveFirstHeadError
  );

  return {
    onDeleteFirstHead,
    onDearchiveFirstHead,
    onArchiveFirstHead,
    onClickAddFirstHead,
    dialogAddFirstHeadDialogProps,
  };
};
